import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, Shield } from "lucide-react";
import AxonMetricHeader from "../../components/layout/AxonMetricHeader.jsx";
import { useAuth } from "../../hooks/useAuth";
import { useAuth as useAuthContext } from "../../context/AuthContext";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword } = useAuth();
  const { login: loginContext } = useAuthContext();
  const [passwords, setPasswords] = useState({ password: "", confirmPassword: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState("");
  const [siteId, setSiteId] = useState(null);

  useEffect(() => {
    const resetToken = searchParams.get('token');
    const siteIdParam = searchParams.get('site_id');
    
    if (resetToken) {
      setToken(resetToken);
    } else {
      setError("Invalid reset link. Please request a new password reset.");
    }
    
    if (siteIdParam) {
      const siteIdNum = parseInt(siteIdParam);
      if (!isNaN(siteIdNum)) {
        setSiteId(siteIdNum);
      } else {
        setError("Invalid site in reset link. Please request a new password reset.");
      }
    } else {
      setError("Site information missing in reset link. Please request a new password reset.");
    }
  }, [searchParams]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");
    
    if (passwords.password.length < 6) {
      setError("Password must be at least 6 characters long.");
      setIsLoading(false);
      return;
    }
    
    if (passwords.password !== passwords.confirmPassword) {
      setError("Passwords do not match.");
      setIsLoading(false);
      return;
    }
    
    if (!siteId) {
      setError("Site information is required. Please use the reset link from your email.");
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await resetPassword(token, passwords.password, passwords.confirmPassword, siteId);
      
      if (response.success) {
        setSuccess("Password reset successful! Redirecting to login...");
        
        // Update AuthContext if user data is returned
        if (response.user && loginContext) {
          try {
            loginContext({
              username: response.user.username || '',
              fullName: response.user.full_name || response.user.first_name + ' ' + response.user.last_name || '',
              role: response.user.role || 'user',
              token: response.token || '',
              email: response.user.email || '',
              avatar: response.user.avatar || response.user.profile_picture || null
            });
          } catch (authError) {
}
        }
        
        setTimeout(() => {
          // Redirect to login page after successful password reset
          navigate('/login');
        }, 2000);
      } else {
        setError(response.error || "Failed to reset password. Please try again.");
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.message || "Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPasswords(prev => ({ ...prev, [field]: value }));
    setError("");
  };

  return (
    <>
      {/* Enhanced CSS Styles */}
      <style>{`
        .glass-morphism {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .brand-glass {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.25) 0%, 
            rgba(255, 255, 255, 0.15) 50%, 
            rgba(0, 132, 207, 0.1) 100%
          );
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .gradient-border {
          background: linear-gradient(135deg, 
            #0084CF 0%, 
            #0084CF 25%, 
            #1C4D88 50%, 
            #0084CF 75%, 
            #0084CF 100%
          );
          background-size: 300% 300%;
          animation: gradientShift 8s ease infinite;
          padding: 2px;
          border-radius: 20px;
          backdrop-filter: blur(20px);
          box-shadow: 0 25px 50px -12px rgba(0, 132, 207, 0.25);
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .inner-content {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(30px);
          border-radius: 18px;
        }

        .background-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-image: url('/assets/login_bg.jpg');
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          z-index: -2;
        }

        .background-overlay {
          display: none;
        }
        
        @keyframes overlayShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .professional-button {
          background: linear-gradient(135deg, 
            #0084CF 0%, 
            #0084CF 50%, 
            #1C4D88 100%
          );
          transition: all 0.3s ease;
        }

        .professional-button:hover {
          background: linear-gradient(135deg, 
            #0066A3 0%, 
            #0066A3 50%, 
            #68d391 100%
          );
          transform: translateY(-2px);
          box-shadow: 0 15px 35px rgba(0, 132, 207, 0.4);
        }
      `}</style>

      {/* Background */}
      <div className="background-container"></div>
      <div className="background-overlay"></div>

      <div className="min-h-screen w-full flex flex-col relative">
        {/* Header Section */}
        <header className="relative z-20 w-full px-6 py-3 h-16 flex items-center">
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/95 backdrop-blur-xl border-b border-gradient-to-r from-[#0084CF]/20 via-[#C2DFF1]/30 to-emerald-200/20"></div>
          <div className="max-w-7xl mx-auto w-full flex justify-between items-center relative z-10">
            <AxonMetricHeader />
          </div>
        </header>

        {/* Main Content */}
        <main className="relative z-10 flex-1 w-full flex items-center justify-center p-6" style={{minHeight: 'calc(100vh - 64px)'}}>
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="w-full max-w-md mx-auto"
          >
            <div className="gradient-border">
              <div className="inner-content p-8 rounded-2xl shadow-2xl">
                
                {/* Reset Password Form */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="mb-6 text-center"
                >
                  <div className="flex items-center justify-center mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0084CF]/20 via-[#C2DFF1]/40 to-[#C2DFF1]/30 rounded-xl shadow-sm border border-[#0084CF]/20 backdrop-blur-sm">
                      <Shield size={24} className="text-[#0084CF]" />
                    </div>
                  </div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-transparent mb-2">
                    Reset Password
                  </h1>
                  <p className="text-gray-700 font-medium">
                    Create a new password for your account
                  </p>
                </motion.div>

                <form onSubmit={handleResetPassword} className="space-y-5">
                  {/* Password Field */}
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    <label htmlFor="reset-new-password" className="block text-sm font-bold mb-2 text-gray-800 flex items-center gap-2">
                      <Lock size={16} className="text-[#0084CF]" aria-hidden="true" />
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        id="reset-new-password"
                        autoComplete="new-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your new password"
                        className="w-full p-3 brand-glass border border-[#0084CF]/30 rounded-xl focus:outline-none transition-all duration-300 font-semibold hover:border-[#0084CF] focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 shadow-lg hover:shadow-xl backdrop-blur-lg text-gray-900 placeholder-gray-600"
                        style={{ color: '#111827', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                        value={passwords.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        disabled={isLoading}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-[#0084CF] transition-colors duration-200"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {passwords.password && (
                      <div className="mt-1">
                        {passwords.password.length < 6 ? (
                          <p className="text-xs text-red-600 flex items-center gap-1">
                            <span className="w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">!</span>
                            </span>
                            Password must be at least 6 characters ({passwords.password.length}/6)
                          </p>
                        ) : (
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <span className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </span>
                            Password length is valid
                          </p>
                        )}
                      </div>
                    )}
                  </motion.div>

                  {/* Confirm Password Field */}
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25, duration: 0.5 }}
                  >
                    <label htmlFor="reset-confirm-password" className="block text-sm font-bold mb-2 text-gray-800 flex items-center gap-2">
                      <Lock size={16} className="text-[#0084CF]" aria-hidden="true" />
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        id="reset-confirm-password"
                        autoComplete="new-password"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm your new password"
                        className="w-full p-3 brand-glass border border-[#0084CF]/30 rounded-xl focus:outline-none transition-all duration-300 font-semibold hover:border-[#0084CF] focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 shadow-lg hover:shadow-xl backdrop-blur-lg text-gray-900 placeholder-gray-600"
                        style={{ color: '#111827', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                        value={passwords.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        disabled={isLoading}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-[#0084CF] transition-colors duration-200"
                      >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {passwords.confirmPassword && (
                      <div className="mt-1">
                        {passwords.password !== passwords.confirmPassword ? (
                          <p className="text-xs text-red-600 flex items-center gap-1">
                            <span className="w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">!</span>
                            </span>
                            Passwords do not match
                          </p>
                        ) : (
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <span className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </span>
                            Passwords match
                          </p>
                        )}
                      </div>
                    )}
                  </motion.div>
                  
                  {error && (
                    <div className="bg-red-50/95 backdrop-blur-sm border border-red-200 rounded-xl p-4">
                      <p className="text-sm text-red-700 font-semibold text-center flex items-center justify-center gap-3">
                        <span className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">!</span>
                        </span>
                        {error}
                      </p>
                    </div>
                  )}

                  {success && (
                    <div className="bg-green-50/95 backdrop-blur-sm border border-green-200 rounded-xl p-4">
                      <p className="text-sm text-green-700 font-semibold text-center flex items-center justify-center gap-3">
                        <span className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </span>
                        {success}
                      </p>
                    </div>
                  )}

                  <motion.button
                    type="submit"
                    disabled={isLoading || !passwords.password || !passwords.confirmPassword || !token || passwords.password !== passwords.confirmPassword || passwords.password.length < 6}
                    whileHover={{ scale: isLoading ? 1 : 1.02 }}
                    whileTap={{ scale: isLoading ? 1 : 0.98 }}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className={`w-full py-3 mt-6 rounded-xl font-bold uppercase tracking-wide transition-all duration-300 flex items-center justify-center shadow-xl ${
                      isLoading || !passwords.password || !passwords.confirmPassword || !token || !siteId || passwords.password !== passwords.confirmPassword || passwords.password.length < 6
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                        : "professional-button text-white shadow-[#0084CF]/40 hover:shadow-[#0084CF]/60 hover:shadow-2xl"
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-4">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Resetting Password...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Shield size={18} />
                        <span>Reset Password</span>
                      </div>
                    )}
                  </motion.button>

                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.5 }}
                    className="text-center mt-6"
                  >
                    <p className="text-sm text-gray-600">
                      Remember your password?{" "}
                      <Link to="/login" className="text-[#0084CF] hover:underline font-semibold">
                        Sign in here
                      </Link>
                    </p>
                  </motion.div>
                </form>
              </div>
            </div>
          </motion.div>
        </main>
      </div>
    </>
  );
};

export default ResetPasswordPage;

